/**
 * faIconMap.js
 *
 * Shared utility that maps FontAwesome CSS class names (fa-*) stored in the
 * database to their equivalent react-icons/fa React components.
 *
 * The project does NOT load the FontAwesome CSS stylesheet, so raw
 * `<i className="fas fa-building">` tags render as invisible blanks.
 * Instead, use `getFaIcon()` or `<FaIcon />` to resolve the stored class
 * name to a real React Icon component.
 *
 * Usage:
 *   import { getFaIcon, FaIcon } from '@/utils/faIconMap';
 *
 *   // Option A – get the component and render manually
 *   const Icon = getFaIcon('fa-building');
 *   return <Icon size={20} color="#3498db" />;
 *
 *   // Option B – convenience wrapper
 *   return <FaIcon icon="fa-building" size={20} color="#3498db" />;
 */

import {
    FaTasks, FaCheck, FaCheckCircle, FaCheckSquare, FaClipboard,
    FaClipboardCheck, FaClipboardList, FaList, FaListAlt, FaListUl,
    FaListOl, FaPen, FaEdit, FaPencilAlt,
    FaExclamation, FaExclamationCircle, FaExclamationTriangle, FaBell,
    FaBellSlash, FaFlag, FaInfo, FaInfoCircle, FaQuestion, FaQuestionCircle,
    FaTimes, FaTimesCircle, FaBan,
    FaFile, FaFileAlt, FaFilePdf, FaFileExcel, FaFileWord, FaFileImage,
    FaFileContract, FaFileInvoice, FaFileInvoiceDollar, FaFolder, FaFolderOpen,
    FaPaperclip, FaReceipt,
    FaBuilding, FaBriefcase, FaChartLine, FaChartBar, FaChartPie,
    FaDollarSign, FaMoneyBill, FaMoneyCheck, FaCreditCard, FaWallet,
    FaPiggyBank, FaCoins, FaPercentage, FaCalculator,
    FaUser, FaUsers, FaUserTie, FaUserCog, FaUserCheck, FaUserPlus,
    FaHandshake, FaComments, FaComment, FaEnvelope, FaPhone,
    FaAddressBook, FaIdCard, FaHeadset,
    FaGavel, FaBalanceScale, FaShieldAlt, FaLock, FaUnlock, FaKey,
    FaStamp, FaCertificate, FaAward, FaUserShield, FaFingerprint,
    FaCogs, FaCog, FaWrench, FaTools, FaHammer, FaScrewdriver,
    FaHardHat, FaIndustry, FaWarehouse, FaTruck, FaBox, FaBoxes, FaDolly,
    FaLaptop, FaDesktop, FaServer, FaDatabase, FaHdd, FaWifi,
    FaNetworkWired, FaCode, FaBug, FaLaptopCode, FaTerminal, FaMicrochip,
    FaLeaf, FaTree, FaSeedling, FaRecycle, FaSolarPanel, FaWind, FaWater,
    FaTint, FaGlobe, FaGlobeAfrica, FaTemperatureHigh, FaThermometerHalf,
    FaBullhorn, FaAd, FaBroadcastTower, FaCamera, FaVideo, FaImage,
    FaImages, FaPhotoVideo, FaNewspaper, FaPalette, FaPaintBrush,
    FaClock, FaCalendar, FaCalendarAlt, FaCalendarCheck, FaCalendarTimes,
    FaCalendarDay, FaCalendarWeek, FaHourglass, FaHourglassHalf,
    FaStopwatch, FaHistory, FaSync, FaRedo,
    FaStar, FaHeart, FaBolt, FaFire, FaLightbulb, FaTrophy, FaMedal,
    FaGem, FaCrown, FaBookmark, FaTag, FaTags, FaThumbsUp, FaThumbsDown,
    FaSitemap, FaMoneyBillWave, FaLayerGroup, FaCloud, FaHome,
    FaBoxOpen, FaPlug, FaPrint, FaMobile, FaBook, FaProjectDiagram,
    FaShoppingCart, FaTicketAlt, FaCircle
} from 'react-icons/fa';
import React from 'react';

/**
 * Map of FontAwesome CSS class names → react-icons/fa components.
 * Covers every icon available in the Common/IconPicker categories.
 */
const FA_ICON_MAP = {
    // Actions & Tasks
    'fa-tasks': FaTasks,
    'fa-check': FaCheck,
    'fa-check-circle': FaCheckCircle,
    'fa-check-square': FaCheckSquare,
    'fa-clipboard': FaClipboard,
    'fa-clipboard-check': FaClipboardCheck,
    'fa-clipboard-list': FaClipboardList,
    'fa-list': FaList,
    'fa-list-alt': FaListAlt,
    'fa-list-ul': FaListUl,
    'fa-list-ol': FaListOl,
    'fa-pen': FaPen,
    'fa-edit': FaEdit,
    'fa-pencil-alt': FaPencilAlt,

    // Alerts & Status
    'fa-exclamation': FaExclamation,
    'fa-exclamation-circle': FaExclamationCircle,
    'fa-exclamation-triangle': FaExclamationTriangle,
    'fa-bell': FaBell,
    'fa-bell-slash': FaBellSlash,
    'fa-flag': FaFlag,
    'fa-info': FaInfo,
    'fa-info-circle': FaInfoCircle,
    'fa-question': FaQuestion,
    'fa-question-circle': FaQuestionCircle,
    'fa-times': FaTimes,
    'fa-times-circle': FaTimesCircle,
    'fa-ban': FaBan,

    // Documents & Files
    'fa-file': FaFile,
    'fa-file-alt': FaFileAlt,
    'fa-file-pdf': FaFilePdf,
    'fa-file-excel': FaFileExcel,
    'fa-file-word': FaFileWord,
    'fa-file-image': FaFileImage,
    'fa-file-contract': FaFileContract,
    'fa-file-invoice': FaFileInvoice,
    'fa-file-invoice-dollar': FaFileInvoiceDollar,
    'fa-folder': FaFolder,
    'fa-folder-open': FaFolderOpen,
    'fa-paperclip': FaPaperclip,
    'fa-receipt': FaReceipt,

    // Business & Finance
    'fa-building': FaBuilding,
    'fa-briefcase': FaBriefcase,
    'fa-chart-line': FaChartLine,
    'fa-chart-bar': FaChartBar,
    'fa-chart-pie': FaChartPie,
    'fa-dollar-sign': FaDollarSign,
    'fa-money-bill': FaMoneyBill,
    'fa-money-bill-wave': FaMoneyBillWave,
    'fa-money-check': FaMoneyCheck,
    'fa-credit-card': FaCreditCard,
    'fa-wallet': FaWallet,
    'fa-piggy-bank': FaPiggyBank,
    'fa-coins': FaCoins,
    'fa-percentage': FaPercentage,
    'fa-calculator': FaCalculator,

    // People & Communication
    'fa-user': FaUser,
    'fa-users': FaUsers,
    'fa-user-tie': FaUserTie,
    'fa-user-cog': FaUserCog,
    'fa-user-check': FaUserCheck,
    'fa-user-plus': FaUserPlus,
    'fa-handshake': FaHandshake,
    'fa-comments': FaComments,
    'fa-comment': FaComment,
    'fa-envelope': FaEnvelope,
    'fa-phone': FaPhone,
    'fa-address-book': FaAddressBook,
    'fa-id-card': FaIdCard,
    'fa-headset': FaHeadset,

    // Legal & Compliance
    'fa-gavel': FaGavel,
    'fa-balance-scale': FaBalanceScale,
    'fa-shield-alt': FaShieldAlt,
    'fa-lock': FaLock,
    'fa-unlock': FaUnlock,
    'fa-key': FaKey,
    'fa-stamp': FaStamp,
    'fa-certificate': FaCertificate,
    'fa-award': FaAward,
    'fa-user-shield': FaUserShield,
    'fa-fingerprint': FaFingerprint,

    // Operations & Tools
    'fa-cogs': FaCogs,
    'fa-cog': FaCog,
    'fa-wrench': FaWrench,
    'fa-tools': FaTools,
    'fa-hammer': FaHammer,
    'fa-screwdriver': FaScrewdriver,
    'fa-hard-hat': FaHardHat,
    'fa-industry': FaIndustry,
    'fa-warehouse': FaWarehouse,
    'fa-truck': FaTruck,
    'fa-box': FaBox,
    'fa-boxes': FaBoxes,
    'fa-box-open': FaBoxOpen,
    'fa-dolly': FaDolly,

    // Technology & IT
    'fa-laptop': FaLaptop,
    'fa-laptop-code': FaLaptopCode,
    'fa-desktop': FaDesktop,
    'fa-server': FaServer,
    'fa-database': FaDatabase,
    'fa-hdd': FaHdd,
    'fa-wifi': FaWifi,
    'fa-network-wired': FaNetworkWired,
    'fa-code': FaCode,
    'fa-bug': FaBug,
    'fa-terminal': FaTerminal,
    'fa-microchip': FaMicrochip,
    'fa-cloud': FaCloud,
    'fa-mobile': FaMobile,
    'fa-print': FaPrint,
    'fa-plug': FaPlug,

    // Environment & Sustainability
    'fa-leaf': FaLeaf,
    'fa-tree': FaTree,
    'fa-seedling': FaSeedling,
    'fa-recycle': FaRecycle,
    'fa-solar-panel': FaSolarPanel,
    'fa-wind': FaWind,
    'fa-water': FaWater,
    'fa-tint': FaTint,
    'fa-globe': FaGlobe,
    'fa-globe-africa': FaGlobeAfrica,
    'fa-temperature-high': FaTemperatureHigh,
    'fa-thermometer-half': FaThermometerHalf,

    // Marketing & Media
    'fa-bullhorn': FaBullhorn,
    'fa-ad': FaAd,
    'fa-broadcast-tower': FaBroadcastTower,
    'fa-camera': FaCamera,
    'fa-video': FaVideo,
    'fa-image': FaImage,
    'fa-images': FaImages,
    'fa-photo-video': FaPhotoVideo,
    'fa-newspaper': FaNewspaper,
    'fa-palette': FaPalette,
    'fa-paint-brush': FaPaintBrush,

    // Time & Scheduling
    'fa-clock': FaClock,
    'fa-calendar': FaCalendar,
    'fa-calendar-alt': FaCalendarAlt,
    'fa-calendar-check': FaCalendarCheck,
    'fa-calendar-times': FaCalendarTimes,
    'fa-calendar-day': FaCalendarDay,
    'fa-calendar-week': FaCalendarWeek,
    'fa-hourglass': FaHourglass,
    'fa-hourglass-half': FaHourglassHalf,
    'fa-stopwatch': FaStopwatch,
    'fa-history': FaHistory,
    'fa-sync': FaSync,
    'fa-redo': FaRedo,

    // Misc & Symbols
    'fa-star': FaStar,
    'fa-heart': FaHeart,
    'fa-bolt': FaBolt,
    'fa-fire': FaFire,
    'fa-lightbulb': FaLightbulb,
    'fa-trophy': FaTrophy,
    'fa-medal': FaMedal,
    'fa-gem': FaGem,
    'fa-crown': FaCrown,
    'fa-bookmark': FaBookmark,
    'fa-tag': FaTag,
    'fa-tags': FaTags,
    'fa-thumbs-up': FaThumbsUp,
    'fa-thumbs-down': FaThumbsDown,

    // Additional common icons
    'fa-sitemap': FaSitemap,
    'fa-layer-group': FaLayerGroup,
    'fa-home': FaHome,
    'fa-book': FaBook,
    'fa-project-diagram': FaProjectDiagram,
    'fa-shopping-cart': FaShoppingCart,
    'fa-ticket-alt': FaTicketAlt,
    'fa-circle': FaCircle,
};

/**
 * Resolves a FontAwesome CSS class name (e.g. "fa-building") to its
 * react-icons/fa component.  Returns `FaQuestionCircle` as a fallback
 * when the class name is not in the map.
 *
 * @param {string} faClassName - The FontAwesome class (e.g. "fa-building")
 * @returns {React.ComponentType} The React Icon component
 */
export function getFaIcon(faClassName) {
    if (!faClassName) return FaQuestionCircle;
    return FA_ICON_MAP[faClassName] || FaQuestionCircle;
}

/**
 * Convenience component that renders a React Icon for a given FA class name.
 *
 * @param {Object} props
 * @param {string} props.icon   - FontAwesome class name (e.g. "fa-building")
 * @param {number} [props.size] - Icon size in pixels
 * @param {string} [props.color]- Icon colour
 * @param {string} [props.className] - Additional CSS class
 * All other props are forwarded to the underlying icon component.
 */
export function FaIcon({ icon, ...rest }) {
    return React.createElement(getFaIcon(icon), rest);
}

export { FA_ICON_MAP };
export default getFaIcon;
